'use client';
import React, { useState, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import { listDiamonds, useEntityList } from '@/lib/data';
import type { Diamond } from '@/lib/database';

const SPEC_KEYS: { key: keyof Diamond; label: string; format?: (v: unknown) => string }[] = [
  { key: 'shape', label: 'Shape' },
  { key: 'carat', label: 'Carat', format: (v) => Number(v).toFixed(2) },
  { key: 'color', label: 'Color' },
  { key: 'clarity', label: 'Clarity' },
  { key: 'cut', label: 'Cut' },
  { key: 'polish', label: 'Polish' },
  { key: 'symmetry', label: 'Symmetry' },
  { key: 'fluorescence', label: 'Fluorescence' },
  { key: 'depth_pct', label: 'Depth %', format: (v) => `${v}%` },
  { key: 'table_pct', label: 'Table %', format: (v) => `${v}%` },
  { key: 'measurements', label: 'Measurements' },
  { key: 'cert_body', label: 'Cert Body' },
  { key: 'cert_number', label: 'Cert #' },
  { key: 'location', label: 'Location' },
  { key: 'status', label: 'Status' },
  { key: 'cost_usd', label: 'Cost', format: (v) => `$${Number(v).toLocaleString()}` },
  { key: 'price_usd', label: 'Ask Price', format: (v) => `$${Number(v).toLocaleString()}` },
  {
    key: 'rapaport_price_usd',
    label: 'Rap Price',
    format: (v) => `$${Number(v).toLocaleString()}`,
  },
];

const GRADE_ORDER: Record<string, string[]> = {
  cut: ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'],
  polish: ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'],
  symmetry: ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'],
  clarity: ['IF', 'FL', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'I1', 'I2', 'I3'],
  color: ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'],
  fluorescence: ['None', 'Faint', 'Medium', 'Strong', 'Very Strong'],
};

function gradeScore(key: string, val: string): number {
  const order = GRADE_ORDER[key];
  if (!order) return -1;
  const idx = order.indexOf(val);
  return idx === -1 ? 999 : idx;
}

function isBestValue(key: keyof Diamond, values: (string | number | boolean)[]): boolean[] {
  const numericBest = ['carat', 'price_usd', 'cost_usd', 'rapaport_price_usd'];
  const gradedBest = Object.keys(GRADE_ORDER);
  if (numericBest.includes(key as string)) {
    const nums = values.map((v) => Number(v));
    const max = Math.max(...nums);
    return nums.map((n) => n === max);
  }
  if (gradedBest.includes(key as string)) {
    const scores = values.map((v) => gradeScore(key as string, String(v)));
    const best = Math.min(...scores);
    return scores.map((s) => s === best);
  }
  return values.map(() => false);
}

const SLOT_COLORS = [
  'border-indigo-500/60 bg-indigo-500/5',
  'border-violet-500/60 bg-violet-500/5',
  'border-cyan-500/60 bg-cyan-500/5',
  'border-amber-500/60 bg-amber-500/5',
];
const SLOT_HEADER_COLORS = [
  'text-indigo-400',
  'text-violet-400',
  'text-cyan-400',
  'text-amber-400',
];

export default function DiamondComparisonContent() {
  const { items: diamonds } = useEntityList(listDiamonds);
  const [selectedIds, setSelectedIds] = useState<(string | null)[]>([null, null, null, null]);
  const [search, setSearch] = useState('');
  const [activeSlot, setActiveSlot] = useState<number | null>(null);

  const available = diamonds.filter(
    (d) =>
      d.status === 'AVAILABLE' ||
      d.status === 'CERTIFIED' ||
      d.status === 'ON_MEMO' ||
      d.status === 'ON_HOLD'
  );

  const filtered = useMemo(() => {
    if (!search) return available;
    const q = search.toLowerCase();
    return available.filter(
      (d) =>
        d.sku.toLowerCase().includes(q) ||
        d.shape.toLowerCase().includes(q) ||
        d.color.toLowerCase().includes(q) ||
        d.clarity.toLowerCase().includes(q)
    );
  }, [search]);

  const selectedDiamonds = selectedIds.map((id) =>
    id ? (diamonds.find((d) => d.id === id) ?? null) : null
  );
  const filledCount = selectedDiamonds.filter(Boolean).length;

  const handleSelect = (id: string) => {
    if (activeSlot === null) return;
    const updated = [...selectedIds];
    updated[activeSlot] = id;
    setSelectedIds(updated);
    setActiveSlot(null);
    setSearch('');
  };

  const handleRemove = (slot: number) => {
    const updated = [...selectedIds];
    updated[slot] = null;
    setSelectedIds(updated);
  };

  const handleClear = () => {
    setSelectedIds([null, null, null, null]);
    setActiveSlot(null);
    setSearch('');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Diamond Comparison</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Compare up to 4 diamonds side-by-side with spec highlights
          </p>
        </div>
        {filledCount > 0 && (
          <button
            onClick={handleClear}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
          >
            <Icon name="XMarkIcon" size={14} />
            Clear All
          </button>
        )}
      </div>

      {/* Slot Picker Row */}
      <div className="grid grid-cols-4 gap-3">
        {selectedIds.map((id, slot) => {
          const diamond = id ? diamonds.find((d) => d.id === id) : null;
          const isActive = activeSlot === slot;
          return (
            <div
              key={slot}
              className={`rounded-xl border-2 transition-all duration-200 ${isActive ? SLOT_COLORS[slot] + ' ring-2 ring-offset-1 ring-offset-background ring-indigo-500/40' : diamond ? SLOT_COLORS[slot] : 'border-dashed border-border bg-card/40'}`}
            >
              {diamond ? (
                <div className="p-3 space-y-1">
                  <div className="flex items-start justify-between gap-1">
                    <span className={`text-xs font-bold truncate ${SLOT_HEADER_COLORS[slot]}`}>
                      {diamond.sku}
                    </span>
                    <button
                      onClick={() => handleRemove(slot)}
                      className="text-muted-foreground hover:text-danger flex-shrink-0"
                    >
                      <Icon name="XMarkIcon" size={14} />
                    </button>
                  </div>
                  <p className="text-sm font-semibold text-foreground">
                    {diamond.carat.toFixed(2)}ct {diamond.color} {diamond.clarity}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {diamond.shape} · {diamond.cut}
                  </p>
                  <p className="text-sm font-bold text-emerald-400">
                    ${diamond.price_usd.toLocaleString()}
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => setActiveSlot(slot)}
                  className="w-full h-full min-h-[90px] flex flex-col items-center justify-center gap-1.5 p-3 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="PlusCircleIcon" size={22} />
                  <span className="text-xs font-medium">Slot {slot + 1}</span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Diamond Picker Panel */}
      {activeSlot !== null && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">
              Select diamond for{' '}
              <span className={SLOT_HEADER_COLORS[activeSlot]}>Slot {activeSlot + 1}</span>
            </p>
            <button
              onClick={() => {
                setActiveSlot(null);
                setSearch('');
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="XMarkIcon" size={16} />
            </button>
          </div>
          <div className="relative">
            <Icon
              name="MagnifyingGlassIcon"
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by SKU, shape, color, clarity…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
            {filtered
              .filter((d) => !selectedIds.includes(d.id))
              .map((d) => (
                <button
                  key={d.id}
                  onClick={() => handleSelect(d.id)}
                  className="text-left p-2.5 rounded-lg border border-border bg-background hover:border-indigo-500/50 hover:bg-indigo-500/5 transition-all"
                >
                  <p className="text-xs font-semibold text-indigo-400 truncate">{d.sku}</p>
                  <p className="text-xs text-foreground mt-0.5">
                    {d.carat.toFixed(2)}ct {d.color} {d.clarity}
                  </p>
                  <p className="text-xs text-muted-foreground">{d.shape}</p>
                  <p className="text-xs font-bold text-emerald-400 mt-0.5">
                    ${d.price_usd.toLocaleString()}
                  </p>
                </button>
              ))}
            {filtered.filter((d) => !selectedIds.includes(d.id)).length === 0 && (
              <p className="col-span-4 text-center text-sm text-muted-foreground py-4">
                No diamonds match your search
              </p>
            )}
          </div>
        </div>
      )}

      {/* Comparison Table */}
      {filledCount >= 2 && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-32">
                    Spec
                  </th>
                  {selectedDiamonds.map((d, i) =>
                    d ? (
                      <th
                        key={i}
                        className={`px-4 py-3 text-center text-xs font-bold uppercase tracking-wider ${SLOT_HEADER_COLORS[i]}`}
                      >
                        {d.sku}
                      </th>
                    ) : null
                  )}
                </tr>
              </thead>
              <tbody>
                {SPEC_KEYS.map(({ key, label, format }) => {
                  const vals = selectedDiamonds.filter(Boolean).map((d) => d![key]);
                  const bests = isBestValue(key, vals);
                  const allSame = vals.every((v) => String(v) === String(vals[0]));
                  let colIdx = 0;
                  return (
                    <tr
                      key={key}
                      className="border-b border-border/50 hover:bg-background/30 transition-colors"
                    >
                      <td className="px-4 py-2.5 text-xs font-medium text-muted-foreground">
                        {label}
                      </td>
                      {selectedDiamonds.map((d, i) => {
                        if (!d) return null;
                        const raw = d[key];
                        const display = format ? format(raw) : String(raw);
                        const isBest = bests[colIdx++];
                        return (
                          <td
                            key={i}
                            className={`px-4 py-2.5 text-center font-medium transition-colors ${
                              allSame
                                ? 'text-foreground'
                                : isBest
                                  ? 'text-emerald-400 bg-emerald-500/5'
                                  : 'text-foreground'
                            }`}
                          >
                            {display}
                            {!allSame && isBest && (
                              <span className="ml-1 text-emerald-400 text-xs">★</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {/* Price Diff Row */}
                <tr className="bg-background/50 border-t-2 border-border">
                  <td className="px-4 py-3 text-xs font-bold text-muted-foreground uppercase">
                    vs. Cheapest
                  </td>
                  {(() => {
                    const prices = selectedDiamonds.filter(Boolean).map((d) => d!.price_usd);
                    const minPrice = Math.min(...prices);
                    let ci = 0;
                    return selectedDiamonds.map((d, i) => {
                      if (!d) return null;
                      const diff = d.price_usd - minPrice;
                      ci++;
                      return (
                        <td key={i} className="px-4 py-3 text-center">
                          {diff === 0 ? (
                            <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                              Lowest
                            </span>
                          ) : (
                            <span className="text-xs font-bold text-rose-400">
                              +${diff.toLocaleString()}
                            </span>
                          )}
                        </td>
                      );
                    });
                  })()}
                </tr>
                {/* Margin Row */}
                <tr className="bg-background/30">
                  <td className="px-4 py-3 text-xs font-bold text-muted-foreground uppercase">
                    Margin %
                  </td>
                  {selectedDiamonds.map((d, i) => {
                    if (!d) return null;
                    const margin = (((d.price_usd - d.cost_usd) / d.price_usd) * 100).toFixed(1);
                    return (
                      <td
                        key={i}
                        className="px-4 py-3 text-center text-xs font-bold text-amber-400"
                      >
                        {margin}%
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-border bg-background/30 flex items-center gap-2">
            <span className="text-emerald-400 text-xs">★</span>
            <span className="text-xs text-muted-foreground">
              Best value in category (lower grade index or higher carat/price)
            </span>
          </div>
        </div>
      )}

      {filledCount < 2 && (
        <div className="rounded-xl border border-dashed border-border bg-card/30 flex flex-col items-center justify-center py-16 gap-3">
          <Icon name="ScaleIcon" size={40} className="text-muted-foreground/40" />
          <p className="text-muted-foreground font-medium">Select at least 2 diamonds to compare</p>
          <p className="text-xs text-muted-foreground/60">Click the slots above to add diamonds</p>
        </div>
      )}
    </div>
  );
}
